/**
 * El estado de sesión de la SPA.
 *
 * Tres estados y no dos: `cargando` existe para que la primera pintada no muestre la pantalla de
 * acceso a alguien que sí tiene sesión guardada. Sin él, cada recarga daría un parpadeo de «no
 * autenticado» antes de restaurarla.
 *
 * El **rol nunca sale de aquí como fuente de verdad**: esto es una copia para decidir qué pintar.
 * Quien decide qué se puede hacer es el servidor, que lo recalcula en cada petición.
 */

import { create } from 'zustand';
import type { SessionProfile } from '@clica/shared';
import { bootstrapSession, ApiRequestError } from '../api/client.js';
import { applyLocale, detectBrowserLocale } from '../i18n/index.js';
import { supabase } from './supabase.js';

export type SessionState =
  | { readonly status: 'loading' }
  | { readonly status: 'anonymous' }
  | { readonly status: 'rejected'; readonly reason: ApiRequestError }
  /** Algo ha fallado de forma inesperada. Se distingue del rechazo: aquí no es culpa de nadie. */
  | { readonly status: 'failed' }
  | { readonly status: 'active'; readonly profile: SessionProfile };

interface SessionStore {
  readonly state: SessionState;
  readonly refresh: () => Promise<void>;
  readonly setProfile: (profile: SessionProfile) => void;
  readonly signOut: () => Promise<void>;
}

/**
 * El arranque de sesión que está en vuelo, si lo hay.
 *
 * Supabase emite varios eventos al cargar la página —`INITIAL_SESSION`, `SIGNED_IN`, y a menudo
 * un `TOKEN_REFRESHED` justo después—, y cada uno pedía su propio arranque. Se veían tres
 * peticiones idénticas en el mismo milisegundo: trabajo tirado, un arranque más lento justo en
 * lo primero que ve la persona, y una carrera al crear el perfil que devolvía un 500 el primer
 * día de cada usuario.
 *
 * Colapsarlas también evita que dos respuestas lleguen a destiempo y la vieja pise a la nueva.
 */
let enVuelo: Promise<void> | null = null;

export const useSession = create<SessionStore>((set) => ({
  state: { status: 'loading' },

  /**
   * Resuelve el estado de sesión.
   *
   * **Nunca deja el estado en «cargando»**, pase lo que pase. Un fallo inesperado aquí —una
   * respuesta con una forma que no se esperaba, la red caída— dejaría la aplicación colgada en
   * la pantalla de carga para siempre, sin nada que la persona pueda hacer salvo recargar y ver
   * lo mismo. Es preferible una pantalla que explique el fallo y ofrezca reintentar.
   */
  refresh() {
    // Quien llega mientras otro arranque está en curso espera a ese, no lanza el suyo.
    enVuelo ??= resolver().finally(() => {
      enVuelo = null;
    });
    return enVuelo;

    async function resolver(): Promise<void> {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session === null) {
          set({ state: { status: 'anonymous' } });
          return;
        }

        const detected = detectBrowserLocale();
        const { profile } = await bootstrapSession({
          // Solo se usa si el perfil se crea en esta llamada.
          ...(detected === undefined ? {} : { detectedLocale: detected }),
        });
        await applyLocale(profile.locale);
        set({ state: { status: 'active', profile } });
      } catch (error) {
        if (error instanceof ApiRequestError) {
          // Un rechazo del servidor —dominio no permitido, acceso retirado— no deja al usuario
          // en el limbo: se cierra la sesión local para que la pantalla explique qué ha pasado
          // y pueda volver a intentarlo con otra cuenta.
          await supabase.auth.signOut().catch(() => undefined);
          set({ state: { status: 'rejected', reason: error } });
          return;
        }

        console.error('No se ha podido resolver la sesión', error);
        set({ state: { status: 'failed' } });
      }
    }
  },

  setProfile(profile) {
    set({ state: { status: 'active', profile } });
  },

  async signOut() {
    await supabase.auth.signOut();
    set({ state: { status: 'anonymous' } });
  },
}));

/**
 * Conecta el estado con los cambios de sesión de Supabase. Se llama **una sola vez** al arrancar.
 *
 * Sin esta llamada el estado se queda en «cargando» para siempre y la vuelta de Google nunca
 * llega a ninguna parte.
 */
export function listenToAuthChanges(): void {
  void useSession.getState().refresh();

  /*
   * **Suscribirse va dentro de un `try`, y no es paranoia.**
   *
   * `supabase` se crea al primer uso y lanza si faltan sus dos variables de entorno. Esta línea
   * es la primera que lo toca, y estaba fuera de todo `try`: la excepción subía hasta el montaje
   * de React y dejaba **la pantalla completamente en blanco**, sin un solo texto que dijera qué
   * pasaba. Es exactamente la forma de fallo que ya documenta CLAUDE.md con el `switch` sin rama
   * por defecto, por otra vía.
   *
   * Pasa cuando se construye sin las variables puestas —una vista previa, un despliegue mal
   * configurado— y el síntoma no se parece en nada a la causa. Con esto, la pantalla de fallo
   * explica que algo va mal y ofrece reintentar, que es infinitamente más de lo que había.
   */
  try {
    supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT' || event === 'TOKEN_REFRESHED') {
        void useSession.getState().refresh();
      }
    });
  } catch (error) {
    console.error('No se ha podido escuchar los cambios de sesión', error);
    useSession.setState({ state: { status: 'failed' } });
  }
}
